export function getArticleUrl(
  journalAbbr: string,
  documentId: number,
  withPublishUrl: boolean = false,
  extra: string = "",
) {
  const path = `/journals/${journalAbbr}/articles/${documentId}${extra}`;
  return withPublishUrl ? process.env.NEXT_PUBLIC_SITE_URL + path : path;
}

export function getArticlePdfUrl(
  journalAbbr: string,
  documentId: number,
  withPublishUrl: boolean = false,
) {
  return getArticleUrl(journalAbbr, documentId, withPublishUrl, ".pdf");
}

export function getArticleFigureUrl(
  journalAbbr: string,
  documentId: number,
  filaname: string | null = null,
  withPublishUrl: boolean = false,
) {
  const path = `/journals/${journalAbbr}/articles/${documentId}/figures/${filaname ?? ""}`;
  return withPublishUrl ? process.env.NEXT_PUBLIC_SITE_URL + path : path;
}

export function getArticleThumbnailUrl(
  journalAbbr: string,
  documentId: number,
  withPublishUrl: boolean = false,
) {
  return getArticleFigureUrl(
    journalAbbr,
    documentId,
    "thumbnail.webp",
    withPublishUrl,
  );
}

export function isAbsoluteHttpUrl(value?: string | null): boolean {
  return typeof value === "string" && /^https?:\/\//i.test(value);
}

export function normalizeSitePath(pathname?: string | null): string {
  const stripped = (pathname ?? "")
    .replace(/^\/journals\/[^/]+/, "")
    .replace(/\/+$/, "");
  return stripped === "" ? "/" : stripped;
}

export function getArticleThumbnailApiUrl(documentId: number) {
  return `/api/articles/${documentId}/thumbnail`;
}

export function resolveArticleThumbnailSrc(
  article?: {
    id?: number | string | null;
    feature_image_thumbnail?: unknown;
  } | null,
): string | null {
  const thumbnail = Array.isArray(article?.feature_image_thumbnail)
    ? null
    : (article?.feature_image_thumbnail as { content?: unknown } | null);
  const content = thumbnail?.content;

  if (typeof content !== "string" || content.trim() === "") {
    return null;
  }

  if (!content.startsWith("data:")) {
    return content;
  }

  const documentId = Number(article?.id);

  return Number.isInteger(documentId) && documentId > 0
    ? getArticleThumbnailApiUrl(documentId)
    : null;
}

export function getArticleScopusUrl(
  journalAbbr: string,
  documentId: number,
  withPublishUrl: boolean = false,
) {
  return getArticleUrl(journalAbbr, documentId, withPublishUrl, "/scopus");
}

export function getArticleCrossrefUrl(
  journalAbbr: string,
  documentId: number,
  withPublishUrl: boolean = false,
) {
  return getArticleUrl(journalAbbr, documentId, withPublishUrl, "/crossref");
}

export function getArticleOpenAlexUrl(
  journalAbbr: string,
  documentId: number,
  withPublishUrl: boolean = false,
) {
  return getArticleUrl(journalAbbr, documentId, withPublishUrl, "/openalex");
}

export function getArticleOpenCitationsUrl(
  journalAbbr: string,
  documentId: number,
  withPublishUrl: boolean = false,
) {
  return getArticleUrl(
    journalAbbr,
    documentId,
    withPublishUrl,
    "/opencitations",
  );
}

const PUBLIC_CDN_FALLBACK = "https://services.brieflands.com/cdn/serve/";

export const cdnServeUrl = (path?: string | null) => {
  if (!path) return null;

  const configured = process.env.NEXT_PUBLIC_SERVICE_URI_CDN_SERVE;
  const base =
    configured && !configured.includes(".test")
      ? configured
      : PUBLIC_CDN_FALLBACK;

  return `${base}${path}`;
};

export const defaultCoverUrl = () =>
  `${process.env.NEXT_PUBLIC_SITE_URL ?? ""}/images/default-cover.png`;

export const journalCoverUrl = (journal?: {
  meta_data?: { default_cover_image?: string | null } | null;
}) => cdnServeUrl(journal?.meta_data?.default_cover_image) ?? defaultCoverUrl();

export const journalBaseHref = (journal?: {
  journal_url?: string | null;
  path?: string | null;
}) => {
  const raw = journal?.journal_url || (journal?.path ? `journals/${journal.path}` : "");

  if (!raw) return null;

  return raw.startsWith("/") ? raw : `/${raw}`;
};

export const MOST_CITED_SLUG = "most_cited";

export const journalMostCitedHref = (journal?: {
  is_main_site?: boolean;
  journal_url?: string | null;
  path?: string | null;
}) => {
  if (journal?.is_main_site) {
    return `/${MOST_CITED_SLUG}`;
  }

  return `${journalBaseHref(journal) ?? ""}/${MOST_CITED_SLUG}`;
};
